import { Megaphone, Play } from 'lucide-react';
import type { Product } from './types';
import { normalizeProductPricing } from './pricingNormalization';

export const marketingSalesSuite: Product = normalizeProductPricing({
  id: 'marketing-sales-suite',
  title: 'Complete Marketing & Sales Automation Suite',
  subtitle: 'All-in-one marketing & sales platform with AI intelligence',
  description: 'Comprehensive marketing automation, sales enablement, and customer intelligence platform combining CRM, email marketing, social media management, SEO optimisation, predictive analytics, and sales intelligence in one powerful solution designed for UK businesses.',
  icon: Megaphone,
  image: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  category: 'Marketing & Sales',
  status: 'Live',
  businessTypes: ['#enterprise', '#sme', '#marketing', '#sales', '#ecommerce', '#saas', '#agencies'],
  features: [
    // Email Marketing & Automation
    'Advanced Email Campaign Builder with Drag & Drop Interface',
    'Behavioral Email Automation & Trigger-Based Sequences',
    'A/B Testing & Dynamic Content Optimization',
    'Email Deliverability Optimization & Spam Prevention',
    'Advanced Email Analytics & Performance Tracking',
    'Multi-Channel Email Templates & Responsive Design',

    // CRM & Sales Intelligence
    'Complete CRM with Contact & Deal Management',
    'Advanced Lead Scoring & Qualification Algorithms',
    'Sales Pipeline Automation & Forecasting',
    'Customer Journey Mapping & Touchpoint Analysis',
    'Sales Performance Analytics & Team Leaderboards',
    'Revenue Attribution & ROI Tracking',

    // Social Media Management
    'Multi-Platform Social Media Scheduling & Publishing',
    'Social Media Content Calendar & Planning Tools',
    'Social Listening & Brand Mention Monitoring',
    'Influencer Management & Partnership Tracking',
    'Social Media Analytics & Engagement Metrics',
    'Social Commerce Integration & Shoppable Posts',

    // SEO & Content Optimisation
    'Advanced SEO Analytics & Keyword Tracking for UK Search',
    'AI-Powered Content Generation & Optimisation for British Audience',
    'Technical SEO Auditing & Issue Detection with UK Focus',
    'Local SEO & UK Business Directory Management',
    'Competitor SEO Analysis & Gap Identification for UK Market',
    'Link Building Automation & Outreach with UK Publishers',

    // Predictive Analytics & Intelligence
    'AI-Powered Customer Behavior Prediction',
    'Marketing Mix Modeling & Attribution Analysis',
    'Churn Prediction & Customer Retention Analytics',
    'Lifetime Value Optimization & Segmentation',
    'Predictive Lead Scoring with Machine Learning',
    'Market Trend Analysis & Opportunity Identification',

    // Lead Generation & Nurturing
    'Multi-Channel Lead Capture & Form Optimization',
    'Landing Page Builder with A/B Testing',
    'Lead Nurturing Workflows & Drip Campaigns',
    'Progressive Profiling & Data Enrichment',
    'Lead Qualification & Scoring Automation',
    'Integration with Web Analytics & Tracking',

    // Marketing Automation
    'Visual Workflow Builder & Campaign Automation',
    'Cross-Channel Campaign Orchestration',
    'Event-Triggered Marketing & Real-Time Responses',
    'Customer Segmentation & Dynamic List Building',
    'Marketing Funnel Analytics & Conversion Tracking',
    'Personalization Engine & Dynamic Content',

    // Communication & Messaging
    'Unified Inbox for All Customer Communications',
    'WhatsApp Business Integration & Automation',
    'SMS Marketing & Bulk Messaging Campaigns',
    'Live Chat & Chatbot Integration',
    'Video Email & Multimedia Messaging',
    'Communication Templates & Response Automation',

    // Sales Enablement
    'Sales Content Management & Asset Library',
    'Proposal & Quote Generation with E-Signatures',
    'Sales Training & Coaching Analytics',
    'Call Recording & Conversation Intelligence',
    'Meeting Scheduling & Calendar Integration',
    'Sales Playbooks & Process Automation',

    // Customer Success & Retention
    'Customer Health Score Monitoring',
    'Automated Customer Success Workflows',
    'Upsell & Cross-Sell Opportunity Identification',
    'Customer Feedback Collection & Analysis',
    'Loyalty Program Management & Rewards',
    'Customer Support Ticket Integration',

    // Analytics & Reporting
    'Comprehensive Marketing Dashboard & KPIs',
    'Custom Report Builder & Data Visualization',
    'Real-Time Campaign Performance Monitoring',
    'ROI Calculation & Marketing Effectiveness',
    'Attribution Modeling & Channel Performance',
    'Executive Summary Reports & Insights',

    // Integration & API
    'Integration with 200+ Third-Party Platforms',
    'Custom API Development & Webhook Support',
    'Data Import/Export & Migration Tools',
    'Single Sign-On (SSO) & User Management',
    'White-Label Options & Custom Branding',
    'Enterprise-Grade Security & Compliance',

    // Advanced Features
    'AI-Powered Content Recommendations',
    'Predictive Budget Allocation & Optimization',
    'Advanced Customer Journey Analytics',
    'Marketing Automation Workflow Templates',
    'Dynamic Pricing & Promotional Campaign Management',
    'Multi-Location Business Support & Franchise Management',
  ],
  pricing: { setup: 38400, monthly: 1766 },
  color: 'from-purple-600 to-blue-600',
  demoConfig: {
    type: 'interactive',
    url: '/marketing/demo',
    title: 'Experience the Complete Platform',
    description: 'See our comprehensive marketing and sales automation suite in action with real data and workflows',
    buttonText: 'Launch Interactive Demo',
    icon: Play,
    buttonColor: 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700',
  },
});

